import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { loadAllAndAssert, loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';

function generateId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export const createPendingFileMutation = registerMutation({
  operationName: 'createPendingFileMutation',
  type: () => String,
  params: [
    { name: 'examId', type: () => String },
    { name: 'fileIds', type: () => [String] },
    { name: 'name', type: () => String },
    { name: 'mime', type: () => String },
    { name: 'size', type: () => Float },
  ],
  resolve: (examId: string, fileIds: string[], name: string, mime: string, size: number) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    // Staged ids are later read and processed for this exam — only files the caller may read.
    await loadAllAndAssert(AdornisFile, fileIds, 'read', { _id: 1 });
    const examCollection = await getCollection(Exam);

    const existingExam = await examCollection.findOne({ _id: examId });
    if (!existingExam) throw new Error(`Exam ${examId} not found`);

    const id = generateId();
    const pending = {
      id,
      fileIds,
      name,
      mime,
      size,
      status: 'uploaded',
      createdAt: new Date().toISOString(),
    } as any;

    const result = await examCollection.updateOne({ _id: examId }, { $push: { 'examState.pendingFiles': pending } });

    if (result.matchedCount === 0) throw new Error(`Exam ${examId} not found`);

    return id;
  },
});

export const deletePendingFileMutation = registerMutation({
  operationName: 'deletePendingFileMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'pendingFileId', type: () => String },
  ],
  resolve: (examId: string, pendingFileId: string) => async () => {
    await loadAndAssert(Exam, examId, 'update', { _id: 1 });
    const examCollection = await getCollection(Exam);

    const result = await examCollection.updateOne(
      { _id: examId },
      { $pull: { 'examState.pendingFiles': { id: pendingFileId } } },
    );

    if (result.matchedCount === 0) throw new Error(`Exam ${examId} not found`);

    return true;
  },
});
